import { useBasicTripCardData } from "./trip-card/useBasicTripCardData";
import { useShareAndActions } from "./trip-card/useShareAndActions";
import { useWeatherData } from "./trip-card/useWeatherData";
import { useActivitiesData } from "./trip-card/useActivitiesData";
import { useFoodData } from "./trip-card/useFoodData";
import { useLoungeData } from "./trip-card/useLoungeData";
import { useCurrencyData } from "./trip-card/useCurrencyData";
import { useTaxiData } from "./trip-card/useTaxiData";
import { Trip } from "@/types/trip";
import { useIsMobile } from "@/hooks/use-mobile";
import { useEffect } from "react";

export const useTripCardData = (trip: Trip, isFocused = false) => {
  const basic = useBasicTripCardData(trip, isFocused);
  const isMobile = useIsMobile();
  
  const { 
    handleShare, 
    handleDeleteTrip, 
    handleAddToCalendar
  } = useShareAndActions(
    trip, 
    basic.getDestinationName, 
    basic.sortedElements
  );

  // IMPORTANT: Always directly call getDestinationName() function to ensure consistent behavior
  // This ensures we always get the freshest computed value
  const destinationName = basic.getDestinationName();
  const destinationImage = basic.getDestinationImage();
  
  console.debug("🔍 useTripCardData DEBUG:", {
    phase: "HOOK EXECUTION",
    tripId: trip.id,
    platform: isMobile ? 'Mobile' : 'Desktop',
    destinationName,
    mainDestinationCode: basic.mainDestinationCode,
    currentTripDestinationImage: trip.destinationImage,
    freshlyComputedImage: destinationImage,
    areImagesSame: trip.destinationImage === destinationImage,
    timestamp: new Date().toISOString()
  });

  // Add more detailed logging to track caching state
  console.debug("💾 useTripCardData CACHE STATE:", {
    phase: "CACHE CHECK",
    tripId: trip.id,
    platform: isMobile ? 'Mobile' : 'Desktop',
    tripIdentifiedDestination: trip.identifiedDestination,
    tripDestinationImage: trip.destinationImage,
    destinationNameFromFn: destinationName,
    getDestinationImageResult: destinationImage,
    timestamp: new Date().toISOString()
  });

  // Force update the trip's identifiedDestination to ensure consistency
  useEffect(() => {
    if (destinationName && trip.identifiedDestination !== destinationName) {
      const previousImage = trip.destinationImage;
      
      console.debug("🔄 useTripCardData DESTINATION UPDATE:", {
        phase: "DESTINATION CHANGE DETECTED",
        tripId: trip.id,
        platform: isMobile ? 'Mobile' : 'Desktop',
        oldIdentifiedDestination: trip.identifiedDestination,
        newDestinationName: destinationName,
        timestamp: new Date().toISOString()
      });
      
      trip.identifiedDestination = destinationName;
      trip.destinationImage = destinationImage;
      
      console.debug("🖼️ useTripCardData IMAGE UPDATE:", {
        phase: "IMAGE UPDATED",
        tripId: trip.id,
        platform: isMobile ? 'Mobile' : 'Desktop',
        previousImage,
        newImage: trip.destinationImage,
        imageChanged: previousImage !== trip.destinationImage,
        timestamp: new Date().toISOString()
      });
    } else if (!trip.destinationImage && destinationImage) {
      // If we have a computed image but no stored image, update it
      console.debug("🖼️ useTripCardData MISSING IMAGE FIX:", {
        phase: "FIXING MISSING IMAGE",
        tripId: trip.id,
        platform: isMobile ? 'Mobile' : 'Desktop',
        computedImage: destinationImage,
        timestamp: new Date().toISOString()
      });
      
      trip.destinationImage = destinationImage;
    }
  }, [destinationName, destinationImage, trip, isMobile]);

  const { 
    showWeather, 
    setShowWeather, 
    weatherData, 
    handleWeatherClick 
  } = useWeatherData(
    trip.id, 
    basic.firstFlight ? (basic.firstFlight.details as any).arrival.code : basic.mainDestinationCode, 
    destinationName
  );

  const { 
    showActivities, 
    setShowActivities, 
    activitiesData, 
    handleActivitiesClick 
  } = useActivitiesData(
    trip.id, 
    destinationName
  );

  const { 
    showFood, 
    setShowFood, 
    foodData, 
    handleFoodClick 
  } = useFoodData(
    trip.id, 
    destinationName
  );

  const { 
    showLounge, 
    setShowLounge, 
    openLoungeWebview 
  } = useLoungeData(
    trip.id, 
    destinationName
  );

  const { 
    showCurrency, 
    setShowCurrency, 
    currencyData, 
    handleCurrencyClick 
  } = useCurrencyData(
    trip.id, 
    destinationName
  );

  const { 
    showTaxi, 
    setShowTaxi, 
    handleTaxiClick 
  } = useTaxiData(
    trip.id, 
    destinationName
  );

  return {
    ...basic,
    handleShare,
    handleDeleteTrip,
    handleAddToCalendar,
    showWeather,
    setShowWeather,
    weatherData,
    handleWeatherClick,
    showActivities,
    setShowActivities,
    activitiesData,
    handleActivitiesClick,
    showFood,
    setShowFood,
    foodData,
    handleFoodClick,
    showLounge,
    setShowLounge,
    openLoungeWebview,
    showCurrency,
    setShowCurrency,
    currencyData,
    handleCurrencyClick,
    showTaxi,
    setShowTaxi,
    handleTaxiClick
  };
};
